<script setup lang="ts">
import { useScroll } from '@vueuse/core';
import { useTemplateRef, watchEffect } from 'vue';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

// Scroll Shadows
const container = useTemplateRef('container');
const inner = useTemplateRef('inner');
const shadowTop = useTemplateRef('shadowTop');
const shadowBottom = useTemplateRef('shadowBottom');
const { y } = useScroll(inner);

function updateShadows() {
    if (!shadowTop.value || !shadowBottom.value) {
        return;
    }

    const clientHeight = inner.value?.clientHeight ?? 0;
    const scrollHeight = inner.value?.scrollHeight ?? 0;

    container.value?.classList.toggle('scrollable', scrollHeight > clientHeight);
    shadowTop.value.classList.toggle('show', y.value > 0);
    shadowBottom.value.classList.toggle('show', y.value + clientHeight < scrollHeight - 1);
}

watchEffect(() => {
    updateShadows();
});
</script>

<template>
    <div
        ref="container"
        class="scroll-container"
    >
        <div
            ref="shadowTop"
            class="shadow-top"
        ></div>
        <div
            ref="shadowBottom"
            class="shadow-bottom"
        ></div>

        <div
            ref="inner"
            class="inner"
        >
            <slot></slot>
        </div>
    </div>
</template>

<style scoped>
@reference '@/assets/styles/config.css';

.scroll-container {
    --inner-px: var(--spacing);
    --inner-py: 0;
    --inner-scrollbar-margin: var(--spacing);
    position: relative;
    display: flex;
    flex-flow: column;
    background-color: #fff;
    flex: 1 1 auto;
    overflow-y: hidden;

    &.scrollable {
        > .inner {
            padding-right: calc(var(--inner-px) + var(--inner-scrollbar-margin));
        }
    }

    > .inner {
        position: relative;
        flex: 1 1 auto;
        background-color: #fff;
        min-width: 100%;
        max-width: 100%;
        width: fit-content;
        height: 100%;
        padding: var(--inner-py) var(--inner-px);
        overflow: hidden;
        overflow-y: auto;
        z-index: 1;
    }

    .shadow-top,
    .shadow-bottom {
        position: absolute;
        left: 0;
        z-index: 100;
        width: 100%;
        height: 1.5rem;
        z-index: 10;
        pointer-events: none;
        user-select: none;
        opacity: 0;
        transition:
            opacity 0.25s,
            transform 0.25s var(--ease-out-cubic);

        &.show {
            opacity: 1;
            transform: translate(0, 0);
        }
    }

    .shadow-top {
        top: 0;
        background: radial-gradient(farthest-side at 50% 0%, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%) center center;
        background-size: 150% 100%;
        transform: translate(0, -50%);
    }

    .shadow-bottom {
        bottom: 0;
        background: radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%) center
            center;
        background-size: 150% 100%;
        transform: translate(0, 50%);
    }
}
</style>
